import type { BetData } from './discordEmbedHelper'
import { apiRequest } from './axios'
import {
  getName,
  getEmail,
  getEmailVerification,
  getProfileImage,
  type UserAccountData,
  type UserAccountRequest,
  type PrivyUserData,
  type PrivyUserResponse,
} from './helper'
import { type Address } from 'viem'

const updateDiscordChannelApi = async (reqObj: {
  items: BetData[]
  //   wallet_address: `0x${string}`
}) => {
  const url = '/discord'
  try {
    const res = await apiRequest(url, 'POST', { items: reqObj.items })
    return res.data
  } catch (error) {
    throw error
  }
}

const getUserProfileDataApi = async (reqObj: { wallet_address: string }) => {
  const url = '/profile-data'
  try {
    const res = await apiRequest(url, 'GET', {
      wallet_address: reqObj.wallet_address,
    })
    return res.data
  } catch (error) {
    throw error
  }
}

interface ReferralResponse {
  items: { wallet_address: Address }[]
}

const getReferListApi = async (reqObj: {
  invite: string
}): Promise<ReferralResponse> => {
  const url = `/refer-list?invite=${encodeURIComponent(reqObj.invite)}`
  try {
    const res = await apiRequest(url, 'GET')
    return res.data as ReferralResponse
  } catch (error) {
    throw error
  }
}

const updateUserProfileDataApi = async (user: any) => {
  const [firstName, lastName] = getName(user)
  const verification = getEmailVerification(user)

  const reqObj = {
    wallet_address: user.wallet_address,
    email: getEmail(user),
    first_name: firstName,
    last_name: lastName,
    phone: user.phone?.number, // Set only if it was not null at login
    profile_image: getProfileImage(user),
    verify_email_triggered: verification.verify_email_triggered,
    is_verified: verification.is_verified,
  }

  const url = '/profile-data'
  try {
    const res = await apiRequest(url, 'POST', reqObj)
    return res.data
  } catch (error) {
    throw error
  }
}

const createOrUpdateUserAccountApi = async ({
  user,
  walletAddress,
}: {
  user: UserAccountRequest
  walletAddress: string
}): Promise<UserAccountData> => {
  const url = '/account'
  const reqObj: Partial<UserAccountData> = Object.fromEntries(
    Object.entries({
      wallet_address: user.wallet_address,
      login_toolkit: user.login_toolkit,
      last_login_time: user.last_login_time,
      last_signin_at: user.last_signin_at,
      joined_on: user.joined_on,
      signin_token: user.signin_token,
      refer_code: user.refer_code,
      invite_code: user.invite_code,
      utm_source: user.utm_source,
      utm_medium: user.utm_medium,
      utm_campaign: user.utm_campaign,
      utm_term: user.utm_term,
      utm_content: user.utm_content,
      odds_preference: user.odds_preference,
      signed_msg_id: user.signed_msg_id,
    }).filter(([_, value]) => value !== undefined && value !== null) // Remove empty values
  )

  try {
    const res: any = await apiRequest(url, 'POST', { ...reqObj, walletAddress })
    return res.data.user as UserAccountData
  } catch (error) {
    throw error
  }
}

const getUserAccountApi = async (reqObj): Promise<UserAccountData> => {
  const url = '/account'
  try {
    const res: any = await apiRequest(url, 'POST', reqObj)
    return res.data.user as UserAccountData
  } catch (error) {
    throw error
  }
}

const createOrUpdatePrivyUserApi = async (
  user: any
): Promise<PrivyUserResponse> => {
  const url = '/user-privy'
  try {
    // Transform the user object to match database schema
    const userData: PrivyUserData = {
      did: user.id,
      createdAt: new Date(user.createdAt),
      linkedAccounts: JSON.stringify(user.linkedAccounts || []),
      email: user.email ? JSON.stringify(user.email) : null,
      phone: user.phone ? JSON.stringify(user.phone) : null,
      wallet: JSON.stringify(user.wallet),
      google: user.google ? JSON.stringify(user.google) : null,
      apple: user.apple ? JSON.stringify(user.apple) : null,
      twitter: user.twitter ? JSON.stringify(user.twitter) : null,
      discord: user.discord ? JSON.stringify(user.discord) : null,
      telegram: user.telegram ? JSON.stringify(user.telegram) : null,
      github: user.github ? JSON.stringify(user.github) : null,
      linkedin: user.linkedin ? JSON.stringify(user.linkedin) : null,
      spotify: user.spotify ? JSON.stringify(user.spotify) : null,
      instagram: user.instagram ? JSON.stringify(user.instagram) : null,
      tiktok: user.tiktok ? JSON.stringify(user.tiktok) : null,
      farcaster: user.farcaster ? JSON.stringify(user.farcaster) : null,
      delegatedWallets: JSON.stringify(user.delegatedWallets || []),
      mfaMethods: JSON.stringify(user.mfaMethods || []),
      hasAcceptedTerms: user.hasAcceptedTerms || false,
      isGuest: user.isGuest || false,
      wallet_address: user.wallet.address,
    }

    const res = await apiRequest<PrivyUserResponse>(url, 'POST', userData)
    return res.data
  } catch (error) {
    throw error
  }
}

const getPrivyUserApi = async (
  walletAddress: string
): Promise<PrivyUserResponse> => {
  const url = `/user-privy?wallet_address=${walletAddress}`
  try {
    const res = await apiRequest<PrivyUserResponse>(url, 'GET')
    return res.data
  } catch (error) {
    throw error
  }
}

const verifySignInTokenApi = async (data: {
  wallet_address: string
  token: string
}) => {
  const url = '/account/signin'
  try {
    const res = await apiRequest(url, 'POST', data)
    return res.data
  } catch (error) {
    throw error
  }
}

const getSignedRulesStatus = async (
  walletAddress: string
): Promise<{ signed: boolean }> => {
  const url = `/rules/${walletAddress}`
  try {
    const res = await apiRequest<{ signed: boolean }>(url, 'GET')
    return res.data
  } catch (error) {
    throw error
  }
}

const setSignMessage = async ({
  sign_token,
  walletAddress,
}: {
  sign_token: string
  walletAddress?: string
}) => {
  // if (walletAddress) {
  //   const url = '/sign-message-web3'
  //   try {
  //     const res = await apiRequest(url, 'POST', { sign_token, walletAddress })
  //     return res.data
  //   } catch (error) {
  //     throw error
  //   }
  // } else {
  const url = '/sign-message'
  try {
    const res = await apiRequest(url, 'POST', { sign_token })
    return res.data
  } catch (error) {
    throw error
  }
  // }
}

export {
  createOrUpdateUserAccountApi,
  updateDiscordChannelApi,
  getUserProfileDataApi,
  updateUserProfileDataApi,
  createOrUpdatePrivyUserApi,
  getPrivyUserApi,
  verifySignInTokenApi,
  getReferListApi,
  setSignMessage,
  getUserAccountApi,
  getSignedRulesStatus,
}
